import React, { Fragment, useEffect, useState, useMemo } from 'react';
import { Filter, DefaultColumnFilter } from './filters';
import styles from './MmhDataTable.module.scss';

const MmhDataTable = ({ columns, data, renderRowSubComponent, flagForFilter, filterColumns, textAlign='' }) => {
    // State for sorting
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    
    // State for expanded rows
    const [expandedRows, setExpandedRows] = useState(new Set());
    
    // State for pagination
    const [currentPage, setCurrentPage] = useState(0);
    const [pageSize, setPageSize] = useState(30);
    
    // State for filters
    const [filterValues, setFilterValues] = useState({});
    
    // Apply sorting
    const sortedData = useMemo(() => {
        if (!sortConfig.key) return data;
        
        return [...data].sort((a, b) => {
            const aValue = a[sortConfig.key];
            const bValue = b[sortConfig.key];
            
            if (aValue < bValue) {
                return sortConfig.direction === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return sortConfig.direction === 'asc' ? 1 : -1;
            }
            return 0;
        });
    }, [data, sortConfig]);

    // Apply filtering
    const filteredData = useMemo(() => {
        return sortedData.filter(row => {
            return columns.every(column => {
                const filterValue = filterValues[column.id || column.accessor];
                if (!filterValue) return true;
                
                const cellValue = row[column.id || column.accessor];
                if (cellValue === null || cellValue === undefined) return !filterValue;
                return String(cellValue).toLowerCase().includes(filterValue.toLowerCase());
            });
        });
    }, [sortedData, columns, filterValues]);

    // Apply pagination
    const paginatedData = useMemo(() => {
        const start = currentPage * pageSize;
        return filteredData.slice(start, start + pageSize);
    }, [filteredData, currentPage, pageSize]);

    // Handle sorting
    const handleSort = (columnId) => {
        let direction = 'asc';
        if (sortConfig.key === columnId && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key: columnId, direction });
    };

    // Toggle row expansion
    const toggleRowExpansion = (rowIndex) => {
        const newExpandedRows = new Set(expandedRows);
        if (newExpandedRows.has(rowIndex)) {
            newExpandedRows.delete(rowIndex);
        } else {
            newExpandedRows.add(rowIndex);
        }
        setExpandedRows(newExpandedRows);
    };

    // Handle filter change
    const handleFilterChange = (columnId, value) => {
        setFilterValues(prev => ({
            ...prev,
            [columnId]: value
        }));
        setCurrentPage(0); // Reset to first page when filtering
    };

    // Pagination controls
    const totalPages = Math.ceil(filteredData.length / pageSize);
    
    const goToPage = (page) => {
        const validPage = Math.max(0, Math.min(page, totalPages - 1));
        setCurrentPage(validPage);
    };

    const nextPage = () => {
        if (currentPage < totalPages - 1) {
            setCurrentPage(currentPage + 1);
        }
    };

    const previousPage = () => {
        if (currentPage > 0) {
            setCurrentPage(currentPage - 1);
        }
    };

    const [startIndex, setStartIndex] = useState(0);
    const [endIndex, setEndIndex] = useState(0);

    useEffect(() => {
        setStartIndex(0);
        setEndIndex(5);
    }, [startIndex, endIndex])


    return (
        <div className={styles['mmh-data-table__wrapper']}>
            <table className={`${styles['mmh-data-table__table']} report-assessment-table table-checkable no-footer dtr-inline collapsed color-primary`}>
                <thead className={styles['mmh-data-table__head']}>
                <tr className={`${styles['mmh-data-table__row']} ${styles['report-assessment-table-header']}`}>
                    {columns.map((column) => (
                        <th 
                            className={`${styles['mmh-data-table__cell']} ${styles['mmh-data-table__cell--sort']} ${styles['report-assessment-table-header__cell']} ${styles['bg-color-ice-blue']} ${styles['kt-font-bolder']} ${styles['font-size-md']} ${(typeof column.Header === 'string' && column.Header === 'Room') ? styles['joinBtnMinWidth'] : ''}`}
                            onClick={() => handleSort(column.id || column.accessor)}
                        >
                            <div>
                                <div className={`${styles['d-flex']} ${styles['justify-content-center']}`}>
                                    {typeof column.Header === 'function' ? column.Header() : column.Header}
                                </div>
                            </div>
                            {!flagForFilter && (
                                <div style={{ marginTop: 5 }}>
                                    {column.canFilter && (
                                        <input
                                            className={styles['mmh-table__filter-input']}
                                            value={filterValues[column.id || column.accessor] || ''}
                                            onChange={(e) => handleFilterChange(column.id || column.accessor, e.target.value)}
                                            placeholder={`search (${data.length}) ...`}
                                        />
                                    )}
                                </div>
                            )}
                            {flagForFilter && filterColumns?.length > 0 && (
                                filterColumns.map((item, index) => {
                                    return (
                                        <Fragment key={index}>
                                            {column.id === item && (
                                                <div style={{ marginTop: 5 }}>
                                                    {column.canFilter && (
                                                        <input
                                                            className={styles['mmh-table__filter-input']}
                                                            value={filterValues[column.id || column.accessor] || ''}
                                                            onChange={(e) => handleFilterChange(column.id || column.accessor, e.target.value)}
                                                            placeholder={`search (${data.length}) ...`}
                                                        />
                                                    )}
                                                </div>
                                            )}
                                        </Fragment>
                                    )
                                })
                            )}
                        </th>
                    ))}
                </tr>
                </thead>
                <tbody className={styles['mmh-data-table__body']}>
                {paginatedData.map((row, rowIndex) => {
                    const actualIndex = currentPage * pageSize + rowIndex;
                    return (
                        <Fragment key={actualIndex}>
                            <tr 
                                className={`${styles['mmh-data-table__row']} ${styles['kt-font-bold']}`} 
                                style={{ left: "0px" }}
                                onClick={renderRowSubComponent ? () => toggleRowExpansion(actualIndex) : undefined}
                            >
                                {columns.map((column, cellIndex) => {
                                    // Get the cell value - handle both direct access and accessor functions
                                    let cellValue;
                                    if (column.accessor) {
                                        if (typeof column.accessor === 'function') {
                                            cellValue = column.accessor(row, actualIndex);
                                        } else {
                                            cellValue = row[column.accessor];
                                        }
                                    } else {
                                        cellValue = row[column.id];
                                    }
                                    
                                    // Render the cell - handle both direct values and render functions
                                    let renderedCell;
                                    if (column.Cell) {
                                        if (typeof column.Cell === 'function') {
                                            renderedCell = column.Cell({ 
                                                value: cellValue, 
                                                row: { original: row, index: actualIndex }, 
                                                column: column 
                                            });
                                        } else {
                                            renderedCell = cellValue;
                                        }
                                    } else {
                                        renderedCell = typeof cellValue === 'function' ? cellValue() : cellValue;
                                    }
                                    
                                    return (
                                        <td className={`${styles['mmh-data-table__cell']} table-cell`}>
                                            <div className={column.style ? column.style : styles['w-100']}>
                                                <div className={`${styles['d-flex']} ${styles['align-items-center']} ${(textAlign === '' && cellIndex===0) ? `${styles['justify-content-left']}` : `${styles['justify-content-center']}`}`}>
                                                    {renderedCell}
                                                </div>
                                            </div>
                                        </td>
                                    );
                                })}
                            </tr>
                            {expandedRows.has(actualIndex) && renderRowSubComponent && (
                                <tr className={styles['mmh-data-table__row']} style={{ left: "0px" }}>
                                    <td className={styles['mmh-data-table__cell']} colSpan={columns.length}>
                                        <span style={{ width: "118px" }}>
                                            {renderRowSubComponent({ original: row, index: actualIndex })}
                                        </span>
                                    </td>
                                </tr>
                            )}
                        </Fragment>
                    );
                })}
                </tbody>
            </table>
            
            {/* Pagination controls */}
            <div className={styles['mmh-data-table__pagination']}>
                <button onClick={() => previousPage()} disabled={currentPage === 0} className={styles['mmh-data-table__pagination-btn']}>
                    Previous
                </button>
                <span className={styles['mmh-data-table__pagination-info']}>
                    Page {currentPage + 1} of {totalPages}
                </span>
                <button onClick={() => nextPage()} disabled={currentPage === totalPages - 1} className={styles['mmh-data-table__pagination-btn']}>
                    Next
                </button>
                
                <select
                    value={pageSize}
                    onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setCurrentPage(0);
                    }}
                    className={styles['mmh-data-table__pagination-select']}
                >
                    {[10, 20, 30, 40, 50].map(size => (
                        <option key={size} value={size}>
                            Show {size}
                        </option>
                    ))}
                </select>
            </div>
        </div>
    );
};

export default MmhDataTable;
